import { all, call, put, takeLatest, select, takeEvery, take } from "redux-saga/effects";
import { set } from "lodash";

import { cloneDeep } from "../../utils/cloneDeep";

import {
  RECEIVE_APPCONFIG,
  receiveAppConfigSuccess,
  receiveAppConfigFail,
  UPDATE_PROSPECT,
  setConfig,
  updateProspect,
  UPDATE_VIEW_ID,
  saveSignatoryModel,
  saveOrganizationInfoModel,
  UPDATE_ADDITIONAL_INFO,
  SET_ACCORDION_STATUS,
  UPDATE_PROSPECT_FROM_DECISION,
  POST_LOGIN_TOKEN,
  setAccessToken,
  postLoginTokenSuccess,
  postLoginTokenError,
  SET_ACCESS_TOKEN
} from "../actions/appConfig";
import { sendProspectToAPI, sendProspectToAPISuccess } from "../actions/sendProspectToAPI";
import { config, postAuthToken } from "../../api/apiClient";
import {
  UAE_CODE,
  UAE_CURRENCY,
  AUTO,
  AUTH_CLIENT_SECRET,
  AUTH_API_KEY,
  AUTH_CLIENT_ID,
  AUTH_RESOURCE_TYPE
} from "../../constants";
import {
  getIsIslamicBanking,
  getAccountType,
  getLeadSource,
  getRoCode,
  getAdditionalInfoDetailsForBPM,
  getAccordionStatuses,
  getAdditionalInfoDetailsFromBPM,
  getAuthorizationHeader
} from "../selectors/appConfig";
import { log } from "../../utils/loggger";
import { DEVICE_ID } from "../../api/axiosConfig";
import { parseJwt } from "../../utils/genericUtils";

import appConfig from "../../config/appConfig.json";
import { checkLoginStatus } from "../selectors/loginSelector";

const populateAppConfig = configApiResponse => ({
  ...appConfig,
  ...configApiResponse
});

export function* receiveAppConfigSaga() {
  try {
    let response = null;
    const headers = yield select(getAuthorizationHeader);
    const isAgent = yield select(checkLoginStatus);
    if (isAgent) {
      yield take(SET_ACCESS_TOKEN);
      const headers = yield select(getAuthorizationHeader);
      response = yield call(config.postAuthLoad, headers);
    } else {
      response = yield call(config.load, headers);
    }

    const newConfig = populateAppConfig(response.data);
    const signatoryModel = newConfig.prospect
      ? cloneDeep(newConfig.prospect.signatoryInfo[0])
      : null;
    const organizationInfoModel = newConfig.prospect
      ? cloneDeep(newConfig.prospect.organizationInfo)
      : null;

    if (newConfig.prospect) {
      newConfig.prospect.signatoryInfo = [];
      newConfig.prospect.accountInfo.accountCurrency = UAE_CURRENCY;
      if (!newConfig.prospect.applicantInfo.countryCode) {
        newConfig.prospect.applicantInfo.countryCode = UAE_CODE;
      }
      newConfig.prospect.applicantInfo.LeadSource = yield select(getLeadSource);
      newConfig.prospect.applicantInfo.roCode = yield select(getRoCode);
      newConfig.prospect.applicationInfo.accountType = yield select(getAccountType);
      newConfig.prospect.applicationInfo.islamicBanking = yield select(getIsIslamicBanking);
    }

    if (signatoryModel) {
      yield put(saveSignatoryModel(signatoryModel));
    }
    if (organizationInfoModel) {
      yield put(saveOrganizationInfoModel(organizationInfoModel));
    }
    yield put(receiveAppConfigSuccess(newConfig));
    yield put(sendProspectToAPISuccess());
  } catch (error) {
    log(error);
    yield put(receiveAppConfigFail(error));
  }
}

export function* updateProspectSaga(action) {
  const appConfig = yield select(state => state.appConfig);

  // Create a deep clone of appConfig to prevent mutation of nested objects
  const newConfig = cloneDeep(appConfig);

  // Batch updates to minimize state mutations
  const updates = {};
  for (const name in action.payload) {
    // Use updates object to collect all changes
    updates[name] = action.payload[name];
  }

  // Apply all updates at once using set from lodash
  for (const path in updates) {
    set(newConfig, path, updates[path]);
  }

  yield put(setConfig(newConfig));
}

export function* updateViewIdSaga({ payload: { viewId, isSendToApi } }) {
  yield put(updateProspect({ "prospect.applicationInfo.viewId": viewId }));
  if (isSendToApi) {
    yield put(sendProspectToAPI(AUTO));
  }
}

function* updateAdditionInfo({ payload }) {
  try {
    const additionalInfoDetailsForBPM = yield select(getAdditionalInfoDetailsForBPM);

    const requestedFromBPM = yield select(getAdditionalInfoDetailsFromBPM);
    const arr = requestedFromBPM.map(info => info.queryUniqueID);
    const filterRequestedAdditionalInfoDetailsForBPM = additionalInfoDetailsForBPM.filter(info =>
      arr.includes(info.QueryUniqueID)
    );
    // Create a copy of the original array
    const updatedInfoBPM = [...filterRequestedAdditionalInfoDetailsForBPM];

    // Check if a matching QueryUniqueID exists in updatedInfoBPM
    const existingIndex = updatedInfoBPM.findIndex(
      info => info.QueryUniqueID === payload.QueryUniqueID
    );

    if (existingIndex !== -1) {
      // If it exists, update the QueryResponse in the copy
      updatedInfoBPM[existingIndex].QueryResponse = payload.QueryResponse;
    } else {
      // If it doesn't exist, add a new object to the copy
      updatedInfoBPM.push({
        QueryUniqueID: payload.QueryUniqueID,
        QueryResponse: payload.QueryResponse
      });
    }
    yield put(
      updateProspect({ "prospect.additionalDataForBPM.additionalInfoBPM": updatedInfoBPM })
    );
  } catch (e) {
    log(e);
  }
}

function* setAccordionStatus({ payload }) {
  try {
    const accordionsStatus = yield select(getAccordionStatuses);
    const statuses = JSON.parse(accordionsStatus);
    const updatedStatuses = { ...statuses, ...payload };
    yield put(updateProspect({ "prospect.accordionsStatus": JSON.stringify(updatedStatuses) }));
  } catch (e) {
    log(e);
  }
}

export function* postLoginToken(prospectId, selectedSignatoryId) {
  try {
    const headers = yield select(getAuthorizationHeader);
    const { deviceId: uniqueId = "" } = parseJwt(headers.headers.Authorization);
    const authTokenData = {
      client_id: AUTH_CLIENT_ID,
      resource_type: AUTH_RESOURCE_TYPE,
      client_secret: AUTH_CLIENT_SECRET,
      device_id: DEVICE_ID,
      apiKey: AUTH_API_KEY,
      unique_id: uniqueId,
      prospectId: null,
      resource_id: prospectId,
      ...(selectedSignatoryId && { sub_resource_id: selectedSignatoryId })
    };
    const { data } = yield call(postAuthToken.get, authTokenData, headers);
    yield put(setAccessToken(data.access_token, data.refresh_token));
    yield put(postLoginTokenSuccess(data));
  } catch (error) {
    log(error);
    yield put(postLoginTokenError(error));
  }
}

export default function* appConfigSaga() {
  yield all([
    takeLatest(RECEIVE_APPCONFIG, receiveAppConfigSaga),
    takeLatest(UPDATE_PROSPECT, updateProspectSaga),
    takeLatest(UPDATE_VIEW_ID, updateViewIdSaga),
    takeLatest(UPDATE_ADDITIONAL_INFO, updateAdditionInfo),
    takeLatest(SET_ACCORDION_STATUS, setAccordionStatus),
    takeEvery(UPDATE_PROSPECT_FROM_DECISION, updateProspectSaga),
    takeLatest(POST_LOGIN_TOKEN, postLoginToken)
  ]);
}
